import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Plus, Trash2 } from "lucide-react";
import css from "./AddTemplate.module.css";
import Svg from "../../../../../Components/Svg/Svg";
import SvgOptions from "../../../../../Components/Svg/SvgOptions";
import SelectQuestionsModal from "./SelectQuestionModal";
import Button from "../../../../../Components/Button/Button";
import { Get, Post, Put } from "../../../../../Services/Server";
import Url from "../../../../../Services/Url";
import { toast } from "react-toastify";

const AddTemplate = () => {
	const navigate = useNavigate();
	const { state } = useLocation();
	const editingTemplate = state || null; // template passed when editing

	const [modalOpen, setModalOpen] = useState(false);
	const [selectedQuestions, setSelectedQuestions] = useState([]);
	const [taskTypes, setTaskTypes] = useState([]);
	const [templates, setTemplates] = useState([]); // existing templates
	const [questions, setQuestions] = useState([]);

	const [form, setForm] = useState({
		name: "",
		taskTypeId: "",
		usecase: "",
	});

	// Load task types, templates & questions
	useEffect(() => {
		Get(Url.taskType)
			.then(setTaskTypes)
			.catch(() => toast.error("Failed to load task types"));

		Get(Url.riskTemplate.getAllQuestions)
			.then(setQuestions)
			.catch(() => toast.error("Failed to load questions"));

		Get(Url.riskTemplate.getTemplates)
			.then(setTemplates)
			.catch(() => toast.error("Failed to load templates"));
	}, []);

	// Populate form when editing
	useEffect(() => {
		if (!editingTemplate) return;

		setForm({
			name: editingTemplate?.name || "",
			taskTypeId: editingTemplate?.taskType?.id || "",
			usecase: editingTemplate?.usecase || "",
		});

		setSelectedQuestions(editingTemplate.questionIds || []);
	}, [editingTemplate]);

	const update = (field, value) =>
		setForm((prev) => ({ ...prev, [field]: value }));

	// Handle selecting questions modal
	const handleSelectQuestions = (questions) => {
		setSelectedQuestions(questions);
	};

	// Check if taskType already has a template
	const taskTypeHasTemplate = (taskTypeId) => {
		return templates.some((t) => t.taskType.id === Number(taskTypeId));
	};

	const handleSubmit = () => {
		if (!form.name || !form.taskTypeId || selectedQuestions.length === 0) {
			toast.error("All fields are required");
			return;
		}

		//  One template per taskType
		if (!editingTemplate) {
			if (taskTypeHasTemplate(form.taskTypeId)) {
				toast.error("A template already exists for this task type.");
				return;
			}
		}

		const payload = {
			name: form.name,
			taskType: { id: Number(form.taskTypeId) },
			usecase: form.usecase,
			questionIds: selectedQuestions.map((q) => ({ id: q.id })),
		};

		if (editingTemplate) {
			Put(Url.riskTemplate.updateTemplateById(editingTemplate.id), payload)
				.then(() => {
					toast.success("Template updated successfully!");
					navigate("../");
				})
				.catch(() => toast.error("Failed to update template"));
			return;
		}

		Post(Url.riskTemplate.createTemplate, payload)
			.then(() => {
				toast.success("Template created successfully!");
				navigate("../");
			})
			.catch((err) => {
				if (err?.status === 409) {
					toast.error("A template already exists for this task type.");
				} else {
					toast.error("Failed to create template");
				}
			});
	};

	return (
		<div className={css.Page}>
			<div className={css.Main}>
				<div className={css.title}>
					<div
						style={{ cursor: "pointer" }}
						className="PHIcon"
						onClick={() => navigate("../")}
					>
						<Svg>{SvgOptions.ArrowBack}</Svg>
					</div>

					<h3>
						{editingTemplate ? "Edit Risk Template" : "Create Risk Template"}
					</h3>
				</div>

				<div className={css.FormContainer}>
					<div className={css.InputWrapper}>
						<label htmlFor="template_name">Name</label>
						<input
							type="text"
							id="template_name"
							value={form.name}
							onChange={(e) => update("name", e.target.value)}
							className={css.input}
						/>
					</div>

					<div className={css.InputWrapper}>
						<label>Task Type</label>
						<select
							value={form.taskTypeId}
							onChange={(e) => {
								const value = e.target.value;
								update("taskTypeId", value);

								// Instant feedback when selecting
								if (!editingTemplate && taskTypeHasTemplate(value)) {
									toast.error("A template already exists for this task type.");
								}
							}}
							className={css.select}
						>
							<option value="">Select task type</option>

							{taskTypes.map((tt) => (
								<option key={tt.id} value={tt.id}>
									{tt.name}
								</option>
							))}
						</select>
					</div>

					<div className={css.InputWrapper}>
						<label>Use Case</label>
						<textarea
							rows={10}
							value={form.usecase}
							onChange={(e) => update("usecase", e.target.value)}
							className={css.textarea}
						/>
					</div>

					<div className={css.InputWrapper}>
						<p style={{ fontWeight: 600, marginBottom: "10px" }}>
							Risk Assessment Questions
						</p>

						{selectedQuestions.length > 0 && (
							<div className={css.SelectedQuestionsBox}>
								{selectedQuestions.map((q) => (
									<div key={q.id} className={css.SelectedQuestion}>
										<div className={css.SelectedQuestionContent}>
											<Svg>{SvgOptions.Moving}</Svg>
										</div>
										<div className={css.SelectedLeft}>{q.questionText}</div>

										<button
											onClick={() =>
												setSelectedQuestions((prev) =>
													prev.filter((item) => item.id !== q.id)
												)
											}
										>
											<Trash2 className={css.DeleteBtn} size={16} />
										</button>
									</div>
								))}
							</div>
						)}

						<Button
							type="button"
							onClick={() => setModalOpen(true)}
							className={`${css.SecondaryBtn} ${css.button}`}
							icon={SvgOptions.Add}
						>
							Add Question
						</Button>
					</div>

					<Button
						className={`${css.PrimaryBtn} ${css.button}`}
						onClick={handleSubmit}
					>
						{editingTemplate ? "Update Template" : "Create Template"}
					</Button>
				</div>
			</div>

			<SelectQuestionsModal
				isOpen={modalOpen}
				onClose={() => setModalOpen(false)}
				onSelect={handleSelectQuestions}
				selectedQuestions={selectedQuestions}
				availableQuestions={questions}
			/>
		</div>
	);
};

export default AddTemplate;
